/**
 * React Query hooks for org-scoped template management.
 *
 * Provides hooks for CRUD, versioning, upload, and field mapping operations.
 * Write operations require admin role.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgTemplatesApi } from '../services/api';
import { orgTemplateKeys } from './orgTemplateKeys';
import type {
  CreateOrgTemplateInput,
  UpdateOrgTemplateInput,
  UpdateFieldMappingsInput,
  OrgTemplateSchema,
  ReferencePdfMode,
} from '../types/orgTemplate';

export function useOrgTemplates(params?: { limit?: number }) {
  return useQuery({
    queryKey: orgTemplateKeys.list(params),
    queryFn: () => orgTemplatesApi.list(params),
  });
}

export function useOrgTemplate(id: string) {
  return useQuery({
    queryKey: orgTemplateKeys.detail(id),
    queryFn: () => orgTemplatesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateOrgTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOrgTemplateInput) => orgTemplatesApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.all });
    },
  });
}

export function useUpdateOrgTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateOrgTemplateInput }) =>
      orgTemplatesApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.all });
      queryClient.invalidateQueries({
        queryKey: orgTemplateKeys.detail(variables.id),
      });
    },
  });
}

export function useDeactivateOrgTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => orgTemplatesApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.all });
    },
  });
}

export function useTemplateVersions(id: string) {
  return useQuery({
    queryKey: orgTemplateKeys.versions(id),
    queryFn: () => orgTemplatesApi.listVersions(id),
    enabled: !!id,
  });
}

/**
 * Two-phase upload flow (replaces the retired one-step confirm flow): a
 * presigned PUT to a staged (not-yet-numbered) S3 key, followed by staging
 * the upload for review. Nothing is activated here — the caller reviews the
 * staged result (validation warnings, rendered sample) before promoting via
 * `usePromoteStagedVersion`.
 */
export function useStageTemplateVersion(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, changeNotes }: { file: File; changeNotes?: string }) => {
      // Step 1: mint a stagedId and get a presigned URL for the staged key.
      const { url, stagedId } = await orgTemplatesApi.stagedUploadUrl(id);

      // Step 2: upload file to S3. fetch resolves on HTTP errors (e.g. an
      // expired presign's 403), so the status must be checked explicitly —
      // staging a docx that never landed would leave a record pointing at
      // nothing.
      const res = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      });
      if (!res.ok) {
        throw new Error(
          `Template upload to S3 failed (${res.status}); nothing was staged`
        );
      }

      // Step 3: stage the uploaded docx for review.
      return orgTemplatesApi.stageVersion(id, { stagedId, changeNotes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.versions(id) });
    },
  });
}

/** Promotes a staged upload to the template's next active version. */
export function usePromoteStagedVersion(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stagedId: string) => orgTemplatesApi.promoteStagedVersion(id, stagedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.versions(id) });
    },
  });
}

/** Discards a staged upload without activating it. */
export function useDiscardStagedVersion(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stagedId: string) => orgTemplatesApi.discardStagedVersion(id, stagedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.versions(id) });
    },
  });
}

export function useRollbackVersion(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (version: number) => orgTemplatesApi.rollbackVersion(id, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.versions(id) });
    },
  });
}

export function useUpdateFieldMappings(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateFieldMappingsInput) => orgTemplatesApi.updateFieldMappings(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
    },
  });
}

export function useUpdateOrgTemplateSchema(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schema: OrgTemplateSchema) => orgTemplatesApi.updateSchema(id, schema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
    },
  });
}

export function useUpdateReferencePdfMode(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mode,
      cleanupOldFiles,
    }: {
      mode: ReferencePdfMode;
      cleanupOldFiles?: boolean;
    }) => orgTemplatesApi.updateReferencePdfMode(id, mode, cleanupOldFiles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.detail(id) });
    },
  });
}

/**
 * Fetch module-specific PDF URL for per-module mode.
 */
export function useModulePdfUrl(
  templateId: string,
  moduleId: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: orgTemplateKeys.modulePdf(templateId, moduleId),
    queryFn: async () => {
      if (!moduleId) return null;
      return orgTemplatesApi.getModulePdfUrl(templateId, moduleId);
    },
    enabled: !!templateId && !!moduleId && enabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 35 * 60 * 1000,
  });
}

/**
 * Fetch contract details PDF URL for per-module mode.
 */
export function useContractDetailsPdfUrl(
  templateId: string,
  enabled = true
) {
  return useQuery({
    queryKey: orgTemplateKeys.contractDetailsPdf(templateId),
    queryFn: () => orgTemplatesApi.getContractDetailsPdfUrl(templateId),
    enabled: !!templateId && enabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 35 * 60 * 1000,
  });
}
